/** Card Library previews for the products page — see cards/previews.tsx contract. */

import { Fragment, useMemo, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useDrill } from "../lib/drill.js";
import { usePeriod } from "../lib/period.js";
import { Tile, Card, CardHead, Explain } from "../components/cards.js";
import { DonutChart } from "../components/DonutChart.js";
import { TrendChart } from "../components/TrendChart.js";
import { RevenueCmToggle, useStickyRevenueCmMode } from "../components/RevenueCmToggle.js";
import { useCmReady } from "../lib/use-cm-ready.js";
import type { Capabilities } from "../lib/card-requirements.js";
import { productSkuLabel } from "../lib/product-sku-label.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

export interface ProductRow {
  skuId: string;
  skuCode: string;
  productName: string;
  variantName: string;
  classificationVariant?: string | null;
  offerLabel?: string | null;
  packSize?: number | null;
  boxesPerSale?: number | null;
  groupName: string; // the organized product-group name (API: COALESCE(pg.name,'Ungrouped'))
  boxes: number; // pack CONTENTS sold = units × boxes_per_sale (a 6-pack = 6)
  lastPrice: number | null;
  units: number;
  revenue: number;
  orders: number;
  cm: number | null; // absent throughout the gross-results pass
  lastSold: string | null;
}

export const money = (n: number | null): string =>
  n === null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: n < 1000 ? 2 : 0 }).format(n);
export const num = (n: number): string => n.toLocaleString();

/** Same query key as ProductsPage — TanStack dedupes, so previews are free when the page is cached. */
function useProducts(): UseQueryResult<{ products: ProductRow[] }> {
  const api = useApiClient();
  const period = usePeriod();
  return useQuery({
    queryKey: ["products", period.cacheKey],
    queryFn: () => api.get<{ products: ProductRow[] }>("/products", { ...period.query }),
  });
}

/** The same period-scoped monthly series as the tile total. A title that says
 * "last 30 days" must never carry an all-time spark underneath it. Shared by
 * ProductsPage and the previews via the same queryKey, so TanStack dedupes it. */
export function useProductCapabilities(): UseQueryResult<Capabilities> {
  const api = useApiClient();
  return useQuery({
    queryKey: ["capabilities"],
    queryFn: () => api.get<Capabilities>("/dashboard/capabilities"),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useProductsSpark(metric: "revenue" | "units" | "boxes", enabled = true): number[] | undefined {
  const api = useApiClient();
  const period = usePeriod();
  const { data } = useQuery({
    queryKey: ["products-spark", metric, period.cacheKey],
    queryFn: () => api.get<{ rows: Array<{ key: string; value: number }> }>("/dashboard/breakdown", { metric, dimension: "month", ...period.query }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  return data?.rows.map((r) => r.value);
}

export function BuyGoodsProductMetricUnavailable({ title, detail }: {
  title: string;
  detail: string;
}): React.ReactElement {
  return (
    <Card style={{ marginBottom: 0, height: "100%" }}>
      <CardHead title={title} />
      <div role="status" style={{ marginTop: 12, color: "var(--body)", fontSize: 13, lineHeight: 1.5 }}>
        <strong>This number is unavailable.</strong> {detail}
      </div>
    </Card>
  );
}

function ProductsSoldTile(): React.ReactElement {
  const { data } = useProducts();
  const period = usePeriod();
  const caps = useProductCapabilities();
  // How many products went out the door — a multi-pack counts as ONE (founder
  // 2026-06-23: "I want the # of PRODUCTS sold, nobody cares how many SKUs").
  const productsSold = (data?.products ?? []).reduce((s, r) => s + r.units, 0);
  if (caps.data?.physicalQuantity !== true) {
    return <BuyGoodsProductMetricUnavailable title="Products sold" detail={caps.data === undefined ? "We are checking physical quantity against the source history." : "The source history does not prove physical quantity."} />;
  }
  return (
    <Tile
      title="Products sold"
      value={data ? num(productsSold) : "—"}
      sub={`sold ${period.label.toLowerCase()}`}
      drill={{ title: "Products sold by month", metric: "units", dimension: "month" }}
      explain={[
        "<b>What it is:</b> How many products customers actually bought, where a multi-pack counts as one item and free $0 giveaway items don't count at all.",
        "<b>How it's calculated:</b> Add up every product's paid sale-units across your catalog (a $0 bonus or BOGO half is excluded).",
        "<b>Example (sample data · year-to-date):</b> Daily Greens 90ct 2,476 + Deep Sleep 99 1,920 + Daily Greens 30ct 2,386 + Omega Gold 2,221 + the rest = 17,262 products sold.",
        "<b>Note:</b> A product filter narrows this. The 7-Day Reset Guide shows 0 because it's always a $0 bonus — free items are deliberately excluded even though it rides along on 1,588 orders.",
      ]}
    />
  );
}

function TotalUnitsTile(): React.ReactElement {
  const { data } = useProducts();
  const period = usePeriod();
  const caps = useProductCapabilities();
  const quantityReady = caps.data?.physicalQuantity === true;
  const spark = useProductsSpark("boxes", quantityReady);
  // The actual individual units inside what sold — a 6-pack counts as 6 (vs
  // "Products sold" which counts it as 1). Pack contents = units × pack size.
  const totalUnits = (data?.products ?? []).reduce((s, r) => s + r.boxes, 0);
  if (!quantityReady) {
    return <BuyGoodsProductMetricUnavailable title="Units sold" detail={caps.data === undefined ? "We are checking physical quantity against the source history." : "The source history does not prove physical quantity."} />;
  }
  return (
    <Tile
      title="Units sold"
      value={data ? num(totalUnits) : "—"}
      sub={`individual units, ${period.label.toLowerCase()}`}
      spark={spark}
      drill={{ title: "Units sold", metric: "boxes", dimension: "month" }}
      explain={[
        "<b>What it is:</b> The individual units that went out the door. A 6-pack counts as 6. A separate sample discounted to $0 also counts because it shipped.",
        "<b>How it's calculated:</b> Multiply each real catalog line by its pack size, then add the results. Literal $0 rows that only list the bottles inside a paid pack stay out, so those bottles are never counted twice.",
        "<b>Example (sample data · year-to-date):</b> Daily Greens 90ct sells in 3-packs, so its 2,476 sales = 7,428 units; Deep Sleep 99 (3-packs) = 5,760; Radiant Collagen 80 (2-packs) = 1,448; everything else single. Total = 24,348 individual units.",
        "<b>Note:</b> Pick a product to narrow this. A separate physical sample counts, even when it is free. A $0 bookkeeping row for units already inside a paid pack does not count again.",
      ]}
    />
  );
}

export function ProductRevenueTrendCard(): React.ReactElement {
  const period = usePeriod();
  const api = useApiClient();
  const drill = useDrill();
  const hasCm = useCmReady();
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-products-trend");
  const cm = mode === "profit" && hasCm;
  const metric = cm ? "product_cm" : "product_revenue";
  const granularity = period.windowDays !== null && period.windowDays <= 45
    ? "day"
    : period.windowDays !== null && period.windowDays <= 120
      ? "week"
      : "month";
  const monthly = useQuery({
    queryKey: ["products-trend", period.cacheKey, metric, granularity],
    queryFn: () => api.get<{ rows: Array<{ key: string; value: number }> }>("/dashboard/breakdown", { metric, dimension: "month", granularity, ...period.query }),
  });
  const requiredLoad = pageLoadState(monthly);
  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your products"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }
  return (
    <Card style={{ marginBottom: 0 }} onClick={() => drill.open({ title: `${cm ? "Contribution margin" : "Revenue"} by month`, metric, dimension: "month" })} className="dd-clickable-card" title="Click to explore">
      <CardHead title={`Product ${cm ? "CM" : "revenue"} over time · ${period.label}`} explain={[
        "<b>What it is:</b> Revenue or contribution margin (CM) over time, so you can see seasonality and whether growth is becoming more profitable. Short windows use days or weeks so the chart has enough detail.",
        "<b>How it's calculated:</b> Revenue adds each paid product line's price times quantity. CM uses those same product lines and subtracts each SKU's variable costs.",
        "<b>Example (sample data · year-to-date, Revenue):</b> Jan about $207K, Feb $207K, Mar $213K, Apr $216K, May $215K, and the partial current month about $101K.",
        "<b>Note:</b> Pick a product to narrow both views. The total matches the Product details table for the same dates. Product revenue excludes shipping and tax and stays before refunds. CM turns on after every selling product has costs.",
      ]}><RevenueCmToggle mode={cm ? "profit" : "revenue"} setMode={setMode} cmDisabled={!hasCm} /></CardHead>
      <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
        <TrendChart loading={monthly.isLoading || monthly.isPlaceholderData} kind="line" pace switchKinds={["line", "area", "step", "stacked-bars", "table", "number"]} chartKey={`product-${metric}-trend`} points={(monthly.data?.rows ?? []).map((r) => ({ label: r.key, a: r.value }))} />
      </div>
    </Card>
  );
}

/** Aggregate the raw catalog by SKU productName → metric (preview fallback). */
function byProduct(products: ProductRow[], metric: "revenue" | "cm" | "units" | "boxes"): { key: string; value: number }[] {
  const m = new Map<string, number>();
  for (const p of products) m.set(p.productName, (m.get(p.productName) ?? 0) + (p[metric] ?? 0));
  return [...m.entries()].map(([key, value]) => ({ key, value })).sort((a, b) => b.value - a.value);
}

function RevenueConcentrationCard(): React.ReactElement {
  const { data } = useProducts();
  const period = usePeriod();
  const hasCm = useCmReady();
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-products-concentration");
  const cm = mode === "profit" && hasCm;
  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title={`How concentrated is ${cm ? "CM" : "revenue"}? · ${period.label}`} explain={[
        "<b>What it is:</b> Each product's share of revenue or contribution margin (CM). One dominant slice means the business depends heavily on that product.",
        "<b>How it's calculated:</b> Revenue adds each product's sales. CM uses those same items and dates, then subtracts each SKU's variable costs.",
        "<b>Example (sample data · year-to-date, Revenue):</b> Daily Greens is about 37% of product revenue, followed by Deep Sleep and Radiant Collagen.",
        "<b>Note:</b> Pick a product to narrow both views. CM turns on after every selling product has costs. These item-level dollars match the product table.",
      ]}><RevenueCmToggle mode={cm ? "profit" : "revenue"} setMode={setMode} cmDisabled={!hasCm} /></CardHead>
      <div style={{ marginTop: 12 }}>
        <DonutChart money slices={byProduct(data?.products ?? [], cm ? "cm" : "revenue")} />
      </div>
    </Card>
  );
}

function UnitsPerProductCard(): React.ReactElement {
  const { data } = useProducts();
  const period = usePeriod();
  const caps = useProductCapabilities();
  if (caps.data?.physicalQuantity !== true) {
    return <BuyGoodsProductMetricUnavailable title="Units sold per product" detail={caps.data === undefined ? "We are checking physical quantity against the source history." : "The source history does not prove physical quantity."} />;
  }
  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title={`Units sold per product · ${period.label}`} explain={[
        "<b>What it is:</b> Each product's share of individual units sold — the volume leaders, which aren't always the revenue leaders. A cheap high-volume product can quietly drive your fulfillment load.",
        "<b>How it's calculated:</b> The same catalog, counting individual units (a 6-pack = 6) per product family, sorted biggest-first.",
        "<b>Example (sample data · year-to-date):</b> Daily Greens family leads on units at 9,814 (a bigger unit-share than revenue-share, thanks to its 3-packs), then Deep Sleep 7,419, Radiant Collagen 3,117, Omega Gold 2,221, Gut Guardian 1,812. Total = 24,348 units.",
        "<b>Note:</b> Pick a product to narrow this. Separate physical samples count. $0 bookkeeping rows for units already inside a paid pack do not count again. This matches the Units sold tile.",
      ]} />
      <div style={{ marginTop: 12 }}>
        <DonutChart money={false} slices={byProduct(data?.products ?? [], "boxes")} />
      </div>
    </Card>
  );
}

type SortCol = "productName" | "lastPrice" | "units" | "revenue" | "cm" | "orders" | "lastSold";

/**
 * The full catalog table (sort/search header row included). Lives here so the
 * Card Library preview and ProductsPage render the SAME component — the page
 * imports it back rather than duplicating the JSX.
 */
interface ProductTableGroup {
  key: string; name: string; units: number; boxes: number; revenue: number; cm: number; orders: number;
  lastSold: string | null; minPrice: number | null; maxPrice: number | null; skus: ProductRow[];
}

export function ProductTable({ showQuantityNotice = true }: {
  showQuantityNotice?: boolean;
} = {}): React.ReactElement {
  const { data, isLoading } = useProducts();
  const api = useApiClient();
  const period = usePeriod();
  const hasCm = useCmReady();
  const caps = useProductCapabilities();
  const showQuantity = caps.data?.physicalQuantity === true;
  const detailedSourceGap = caps.data?.buyGoodsDetailedHistoryGap === true;
  const showCm = hasCm;
  const [q, setQ] = useState("");
  const [sortCol, setSortCol] = useState<SortCol>("revenue");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // True group-level distinct-order counts (COUNT(DISTINCT order_id) per product
  // group) from the same product-summary query the page uses (react-query dedupes
  // it). Per-SKU order counts CAN'T be summed — an order with two SKUs of one
  // product would be counted twice — so the parent row reads the real group count.
  const groupSummary = useQuery({
    queryKey: ["product-summary", period.cacheKey],
    queryFn: () => api.get<{ rows: Array<{ groupName: string; orders: number }> }>("/catalog/product-summary", { ...period.query }),
  });
  const groupOrders = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of groupSummary.data?.rows ?? []) m.set(r.groupName, r.orders);
    return m;
  }, [groupSummary.data]);

  // The table lists PRODUCTS (the organized group when set, else the product name);
  // click a product to expand its SKUs underneath (founder 2026-06-23: "click Daily
  // Greens, see all the SKUs within it"). No "combine" toggle — always grouped.
  const products = useMemo<ProductTableGroup[]>(() => {
    const all = data?.products ?? [];
    const organized = all.some((r) => r.groupName && r.groupName !== "Ungrouped");
    const filtered = all.filter(
      (r) => q === "" || `${r.productName} ${r.groupName} ${r.skuCode} ${r.classificationVariant ?? ""} ${r.offerLabel ?? ""} ${r.packSize ?? ""}`.toLowerCase().includes(q.toLowerCase()),
    );
    const m = new Map<string, ProductTableGroup>();
    for (const r of filtered) {
      const name = organized && r.groupName && r.groupName !== "Ungrouped" ? r.groupName : r.productName;
      let g = m.get(name);
      if (!g) { g = { key: name, name, units: 0, boxes: 0, revenue: 0, cm: 0, orders: 0, lastSold: null, minPrice: null, maxPrice: null, skus: [] }; m.set(name, g); }
      g.units += r.units; g.boxes += r.boxes; g.revenue += r.revenue; g.cm += r.cm ?? 0; g.orders = Math.max(g.orders, r.orders);
      g.skus.push(r);
      if (r.lastSold && (!g.lastSold || r.lastSold > g.lastSold)) g.lastSold = r.lastSold;
      if (r.lastPrice !== null && r.lastPrice !== undefined) {
        g.minPrice = g.minPrice === null ? r.lastPrice : Math.min(g.minPrice, r.lastPrice);
        g.maxPrice = g.maxPrice === null ? r.lastPrice : Math.max(g.maxPrice, r.lastPrice);
      }
    }
    const arr = [...m.values()];
    // Replace the MAX lower-bound with the TRUE group-level distinct-order count
    // where the product summary has it (organized accounts — the founder's case);
    // unmapped/ungrouped rows keep MAX, which never double-counts.
    for (const g of arr) { const real = groupOrders.get(g.name); if (real !== undefined) g.orders = real; }
    const safeSortCol = (sortCol === "units" && !showQuantity) || (sortCol === "cm" && !showCm)
      ? "revenue"
      : sortCol;
    const sortVal = (g: ProductTableGroup): string | number | null =>
      safeSortCol === "productName" ? g.name : safeSortCol === "lastPrice" ? g.maxPrice : safeSortCol === "lastSold" ? g.lastSold : g[safeSortCol];
    arr.sort((a, b) => {
      const av = sortVal(a), bv = sortVal(b);
      const aNull = av === null || av === undefined, bNull = bv === null || bv === undefined;
      if (aNull !== bNull) return aNull ? 1 : -1;
      if (aNull && bNull) return 0;
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    for (const g of arr) g.skus.sort((a, b) => b.revenue - a.revenue);
    return arr;
  }, [data, q, sortCol, sortDir, groupOrders, showQuantity, showCm]);

  const toggleSort = (col: SortCol): void => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir(col === "productName" ? "asc" : "desc"); }
  };
  const toggleExpand = (key: string): void =>
    setExpanded((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const priceLabel = (g: ProductTableGroup): string =>
    g.minPrice === null ? "—" : g.maxPrice !== null && g.maxPrice !== g.minPrice ? `${money(g.minPrice)}–${money(g.maxPrice)}` : money(g.minPrice);
  const skuIdentity = (sku: ProductRow): React.ReactElement => {
    const label = productSkuLabel(sku);
    return (
      <div title={`${label.description}${label.details ? ` · ${label.details}` : ""} · ${label.code}`} style={{ minWidth: 190 }}>
        <div style={{ color: "var(--body)", fontSize: 12.5, lineHeight: 1.35, whiteSpace: "normal" }}>{label.description}</div>
        {label.details && <div style={{ color: "var(--faint)", fontSize: 10.5, lineHeight: 1.35, marginTop: 2, whiteSpace: "normal" }}>{label.details}</div>}
        <div className="dd-num" style={{ color: "var(--faint)", fontSize: 10.5, lineHeight: 1.35, marginTop: 2 }}>{label.code}</div>
      </div>
    );
  };

  return (
    <>
      <h2 style={{ fontSize: 16, margin: "0 0 2px" }}>Product details · {period.label}</h2>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "6px 0 12px", gap: 12, flexWrap: "wrap" }}>
        <span style={{ color: "var(--body)", fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 6 }}>
          Click a column to sort. Open a product to see each offer, pack, and source SKU code.
          <Explain title="Product details" lines={[
            !showCm
              ? "<b>What it is:</b> The products sold during your selected dates, with each product's most recent price, revenue, order count, and last-sold date. Click a product to see its SKUs."
              : "<b>What it is:</b> The products sold or shipped during your selected dates, with each product's most recent price, units, revenue, contribution margin (CM), order count, and last-sold date. Click a product to see its SKUs.",
            !showCm
              ? "<b>How it's calculated:</b> Price is the most recent price charged. Revenue and orders use the selected dates."
              : "<b>How it's calculated:</b> Price is the most recent price for one pack. Packs sold, revenue, CM, orders, and last-sold use the selected dates. A 3-pack counts as one pack sold and three individual units. CM subtracts the product's variable costs. Each order is counted once for its product family.",
            "<b>Example (sample data · year-to-date):</b> Daily Greens expands to GRN-90 at $129 with 2,476 units = $319,404 across 2,460 orders, plus GRN-30 at $49 with 2,386 units = $116,914. Family row totals $436,318. Check: 2,476 × $129 = $319,404 exactly.",
            "<b>Note:</b> Narrow this with search or the product filter. Products with no sales or shipments during these dates stay out. Choose All time to see old products. CM shows “—” until you enter that product's costs in Set up products & margin. Refund rate by product lives on the Operations page.",
          ]} />
        </span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search products…"
          style={{ border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)", borderRadius: "var(--r-sm)", padding: "8px 12px", fontSize: 13, minWidth: 220 }}
        />
      </div>
      <p style={{ margin: "0 0 12px", color: "var(--body)", fontSize: 12.5 }}>
        Revenue is before refunds, without shipping or tax.{showQuantity ? " Packs sold counts a multipack as one sale. Individual units count what is inside each pack." : ""}
      </p>
      {detailedSourceGap && showQuantityNotice && (
        <div role="status" style={{ margin: "0 0 12px", color: "var(--body)", fontSize: 12.5, lineHeight: 1.45 }}>
          Revenue, price, orders, and last sale stay available. Physical quantity is unavailable for this BuyGoods history.
        </div>
      )}

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", boxShadow: "var(--shadow-card)", overflow: "auto", maxHeight: "calc(100vh - 330px)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <SortTh col="productName" cur={sortCol} dir={sortDir} onSort={toggleSort} align="left">Product</SortTh>
              <th style={th("left")}>Offer / SKU</th>
              <SortTh col="lastPrice" cur={sortCol} dir={sortDir} onSort={toggleSort}>Price</SortTh>
              {showQuantity && <SortTh col="units" cur={sortCol} dir={sortDir} onSort={toggleSort}>Packs sold</SortTh>}
              <SortTh col="revenue" cur={sortCol} dir={sortDir} onSort={toggleSort}>Revenue</SortTh>
              {showCm && <SortTh col="cm" cur={sortCol} dir={sortDir} onSort={toggleSort}>CM</SortTh>}
              <SortTh col="orders" cur={sortCol} dir={sortDir} onSort={toggleSort}>Orders</SortTh>
              <SortTh col="lastSold" cur={sortCol} dir={sortDir} onSort={toggleSort}>Last sold</SortTh>
            </tr>
          </thead>
          <tbody>
            {products.map((g) => {
              const open = expanded.has(g.key);
              const multi = g.skus.length > 1;
              return (
                <Fragment key={g.key}>
                  <tr onClick={() => multi && toggleExpand(g.key)} className={multi ? "dd-drill-row" : undefined} style={{ cursor: multi ? "pointer" : "default" }} title={multi ? (open ? "Hide SKUs" : "Click to see its SKUs") : g.name}>
                    <td style={{ ...td(), color: "var(--text)", fontWeight: 600 }}>
                      <span style={{ display: "inline-block", width: 14, color: "var(--faint)" }}>{multi ? (open ? "▾" : "▸") : ""}</span>
                      {g.name}
                    </td>
                    <td style={td()} title={multi ? `${g.skus.length} SKUs` : undefined}>{multi ? `${g.skus.length} SKUs` : g.skus[0] ? skuIdentity(g.skus[0]) : "—"}</td>
                    <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">{priceLabel(g)}</td>
                    {showQuantity && <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">{num(g.units)}</td>}
                    <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">{money(g.revenue)}</td>
                    {showCm && <td style={{ ...td(), textAlign: "right", color: hasCm ? (g.cm < 0 ? "var(--bad)" : "var(--num)") : "var(--body-faint)" }} className="dd-num" title={hasCm && g.revenue > 0 ? `${Math.round((g.cm / g.revenue) * 100)}% margin` : "Add your product costs to see CM"}>{hasCm ? money(g.cm) : "—"}{hasCm && g.revenue > 0 ? <span style={{ color: g.cm < 0 ? "var(--bad)" : "var(--body-faint)", fontSize: 11, marginLeft: 4 }}>{Math.round((g.cm / g.revenue) * 100)}%</span> : null}</td>}
                    <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">{num(g.orders)}</td>
                    <td style={{ ...td(), textAlign: "right", color: "var(--body)" }}>{g.lastSold ?? "—"}</td>
                  </tr>
                  {open && multi && g.skus.map((s) => (
                    <tr key={s.skuId} style={{ background: "var(--surface-2)" }}>
                      <td style={{ ...td(), paddingLeft: 30, color: "var(--body)", fontWeight: 400 }}>{s.classificationVariant || (s.variantName && s.variantName.toLowerCase() !== "default" ? s.variantName : "↳")}</td>
                      <td style={td()}>{skuIdentity(s)}</td>
                      <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">{money(s.lastPrice)}</td>
                      {showQuantity && <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">{num(s.units)}</td>}
                      <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">{money(s.revenue)}</td>
                      {showCm && <td style={{ ...td(), textAlign: "right", color: hasCm && s.cm !== null ? (s.cm < 0 ? "var(--bad)" : "var(--num)") : "var(--body-faint)" }} className="dd-num">{hasCm ? money(s.cm) : "—"}</td>}
                      <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">{num(s.orders)}</td>
                      <td style={{ ...td(), textAlign: "right", color: "var(--body)" }}>{s.lastSold ?? "—"}</td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
            {products.length === 0 && (
              <tr>
                <td colSpan={6 + Number(showQuantity) + Number(showCm)} style={{ ...td(), color: "var(--faint)", textAlign: "center", padding: 24 }}>{isLoading ? "Loading…" : "No products match."}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function SortTh({ col, cur, dir, onSort, align = "right", children }: { col: SortCol; cur: SortCol; dir: "asc" | "desc"; onSort: (c: SortCol) => void; align?: "left" | "right"; children: React.ReactNode }): React.ReactElement {
  const active = cur === col;
  return (
    <th onClick={() => onSort(col)} style={{ ...th(align), cursor: "pointer", userSelect: "none", color: active ? "var(--text)" : "var(--faint)" }}>
      {children}{active ? (dir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );
}

const th = (align: "left" | "right" = "right"): React.CSSProperties => ({ position: "sticky", top: 0, background: "var(--surface)", zIndex: 2, textAlign: align, padding: "11px 14px", color: "var(--faint)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" });
const td = (): React.CSSProperties => ({ padding: "11px 14px", fontSize: 13, borderBottom: "1px solid var(--hair)" });

export const PRODUCTS_PREVIEWS: Record<string, React.ComponentType> = {
  "products-sold": ProductsSoldTile,
  "total-units": TotalUnitsTile,
  "product-revenue-trend": ProductRevenueTrendCard,
  "revenue-concentration": RevenueConcentrationCard,
  "units-per-product": UnitsPerProductCard,
  "product-table": ProductTable,
};
